import type { Invoice } from "@motolynk/api-client";
import { formatDateTime } from "@motolynk/app-core";
import { useTranslation } from "@motolynk/i18n";
import { Button, formatMoney, Icon, Spinner } from "@motolynk/ui";

/** Statuses after which a vendor order has (or soon has) a tax invoice. */
export const INVOICED_STATUSES = ["shipped", "delivered"];

export interface InvoiceLinksProps {
  rows: Invoice[] | undefined;
  loading: boolean;
  failed: boolean;
  /** The PDF's URL for one document (panel-specific endpoint). */
  pdfUrl: (uid: string) => string;
}

/** The GST invoice and credit notes of one vendor order, each with a "Download" link (PDF). */
export function InvoiceLinks({ rows, loading, failed, pdfUrl }: InvoiceLinksProps) {
  const { t, i18n } = useTranslation("invoices");
  if (loading) {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Spinner size={14} label="" /> {t("loading")}
      </p>
    );
  }
  if (failed) return <p className="text-sm text-destructive">{t("loadFailed")}</p>;
  if (!rows || rows.length === 0) {
    return <p className="text-sm text-muted-foreground">{t("pending")}</p>;
  }
  return (
    <ul className="space-y-2" aria-label={t("title")}>
      {rows.map((row) => (
        <li key={row.uid} className="flex flex-wrap items-center justify-between gap-2 text-sm">
          <span>
            <span className="font-medium">
              {t(`kind.${row.kind}`)} {row.number}
            </span>
            <span className="text-muted-foreground">
              {" · "}
              {formatDateTime(row.issued_at, i18n.language)}
              {" · "}
              {formatMoney(row.total, "INR", i18n.language)}
            </span>
          </span>
          <Button asChild variant="outline" size="sm">
            <a href={pdfUrl(row.uid)} download>
              <Icon name="file" size={14} />
              {t("download", { kind: t(`kind.${row.kind}`) })}
            </a>
          </Button>
        </li>
      ))}
    </ul>
  );
}
